/**
 * Roya commerce interactions. See design.md § Motion System.
 *
 *   Add-to-cart success state  — flashes the button that was submitted
 *   Cart total change flash    — signals that the total moved
 *   <sticky-buy-bar>           — compact buy bar once the main CTA scrolls away
 *
 * Everything here is additive: it observes Dawn's own events and DOM rather
 * than replacing any of its behaviour, so a failure degrades to no animation
 * instead of a broken cart.
 */
(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------
     Add to cart — success confirmation
     Dawn publishes cartUpdate after a successful add. We remember which
     button was submitted so the right one confirms when several product
     forms share a page.
     --------------------------------------------------------------- */

  let lastSubmittedButton = null;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.type !== 'add-to-cart-form') return;
      lastSubmittedButton = form.querySelector('[name="add"]');
    },
    true
  );

  const confirmAdd = () => {
    const button = lastSubmittedButton;
    lastSubmittedButton = null;
    if (!button || reduced()) return;

    button.classList.add('is-added');
    window.setTimeout(() => button.classList.remove('is-added'), 1800);
  };

  /* ---------------------------------------------------------------
     Cart total — flash on change.
     Deliberately not a count-up: the value is a server-formatted money
     string, and re-deriving it client-side across currencies risks
     showing a wrong number. The flash conveys the same change safely.
     --------------------------------------------------------------- */

  const flashTotals = () => {
    if (reduced()) return;
    document.querySelectorAll('.totals__total-value').forEach((el) => {
      el.classList.remove('is-updated');
      void el.offsetWidth;
      el.classList.add('is-updated');
    });
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      confirmAdd();
      flashTotals();
    });
  }

  /* ---------------------------------------------------------------
     <sticky-buy-bar>
     Mirrors the real submit button rather than posting its own request,
     so variant selection, quantity and Dawn's error handling all keep
     working untouched.
     --------------------------------------------------------------- */

  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      this.target = document.getElementById(this.dataset.targetButton);
      this.trigger = this.querySelector('[data-sticky-add]');

      if (!this.target) {
        this.remove();
        return;
      }

      if (this.trigger) {
        this.trigger.addEventListener('click', () => this.target.click());
      }

      this.syncDisabled();
      this.watchDisabled();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show once the real button has scrolled out of view, but not
            // before the user has moved down the page at all.
            const past = window.scrollY > 400;
            this.classList.toggle('is-visible', !entry.isIntersecting && past);
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(this.target);
    }

    // Keep the mirror honest about sold-out / unavailable variants.
    syncDisabled() {
      if (!this.trigger) return;
      const disabled = this.target.hasAttribute('disabled');
      this.trigger.toggleAttribute('disabled', disabled);
      this.trigger.textContent = disabled
        ? this.dataset.soldOutText || 'Sold out'
        : this.dataset.addText || 'Add to cart';
    }

    watchDisabled() {
      this.mutationObserver = new MutationObserver(() => this.syncDisabled());
      this.mutationObserver.observe(this.target, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
})();
